<main class="grow" role="content">
  <div class="kt-container-fixed invoice-create-container py-5 lg:py-7.5">
    <div class="flex items-center justify-between gap-3 flex-wrap mb-5">
      <div>
        <h1 class="text-xl font-semibold">Create Invoice</h1>
        <div class="text-sm text-secondary-foreground">Invoices / New</div>
      </div>
      <a class="kt-btn kt-btn-outline" [routerLink]="['/invoices']">Back to Invoices</a>
    </div>

    @if (loadError()) {
      <div class="kt-alert kt-alert-destructive mb-5">{{ loadError() }}</div>
    }

    @if (validationMessage()) {
      <div class="kt-alert kt-alert-destructive mb-5">{{ validationMessage() }}</div>
    }

    <section class="invoice-sheet">
      <header class="invoice-sheet-header">
        <div class="invoice-brand">
          <div class="invoice-brand-mark">INV</div>
          <div>
            <div class="invoice-brand-title">Invoice</div>
            <div class="invoice-brand-sub">Frida Treasury Management</div>
          </div>
        </div>

        <div class="invoice-meta">
          <div class="invoice-meta-row">
            <span class="invoice-meta-label">Invoice #</span>
            <input
              type="text"
              class="kt-input invoice-meta-input"
              placeholder="INV-0001"
              [ngModel]="form().invoiceNumber"
              (ngModelChange)="onFieldChange('invoiceNumber', $event)"
            />
          </div>
          <div class="invoice-meta-row">
            <span class="invoice-meta-label">Issue Date</span>
            <input
              type="date"
              class="kt-input invoice-meta-input"
              [ngModel]="form().issueDate"
              (ngModelChange)="onFieldChange('issueDate', $event)"
            />
          </div>
        </div>
      </header>

      <div class="invoice-parties">
        <div class="invoice-party">
          <div class="invoice-party-label">Billed From (Vendor)</div>
          <select
            class="kt-select"
            [ngModel]="form().vendorId"
            (ngModelChange)="onVendorChange($event)"
          >
            @for (vendor of vendors(); track vendor.id) {
              <option [value]="vendor.id">{{ vendor.name }}</option>
            }
          </select>
          @if (selectedVendor()) {
            <div class="invoice-party-detail">
              <div>{{ selectedVendor()!.vendorTypeName }}</div>
              @if (selectedVendor()!.description) {
                <div class="text-secondary-foreground">{{ selectedVendor()!.description }}</div>
              }
              <div class="text-secondary-foreground">Default currency: {{ selectedVendor()!.defaultCurrency }}</div>
            </div>
          }
        </div>

        <div class="invoice-party">
          <div class="invoice-party-label">Add linked records</div>
          <select
            class="kt-select"
            [ngModel]="form().linkType"
            (ngModelChange)="onLinkTypeChange($event)"
          >
            <option value="Generic" disabled>Select a related record type...</option>
            <option value="Deal ID">Deal ID</option>
            <option value="Security ID">Security ID</option>
            <option value="Program">Program</option>
          </select>

          @if (form().linkType === 'Program') {
            <select
              class="kt-select mt-2"
              [ngModel]="form().linkedBondDocumentationId"
              (ngModelChange)="onProgramChange($event)"
            >
              <option [ngValue]="null">Select a program...</option>
              @for (program of bondDocumentationOptions(); track program.id) {
                <option [ngValue]="program.id">{{ program.name }}</option>
              }
            </select>
          }

          <div class="invoice-party-detail">
            <div class="text-secondary-foreground">{{ linkSelectionSummary() }}</div>
          </div>
        </div>

        <div class="invoice-party invoice-party-amount">
          <div class="invoice-party-label">Amount</div>
          <div class="invoice-amount-row">
            <input
              type="text"
              class="kt-input invoice-ccy-input"
              maxlength="3"
              [ngModel]="form().currency"
              (ngModelChange)="onFieldChange('currency', $event)"
            />
            <input
              type="number"
              min="0"
              step="0.01"
              class="kt-input invoice-amount-input"
              placeholder="0.00"
              [ngModel]="form().amountText"
              (ngModelChange)="onFieldChange('amountText', $event)"
            />
          </div>
          <div class="invoice-party-detail">
            <div class="text-secondary-foreground">Leave empty to use allocated total.</div>
          </div>
        </div>
      </div>

      <div class="invoice-lines">
        <div class="invoice-lines-header">
          <span>Invoice Lines</span>
          <div class="flex items-center gap-3">
            <span class="invoice-required-chip">Required</span>
            <button type="button" class="kt-btn kt-btn-sm kt-btn-outline" (click)="addGenericLine()">Add generic line</button>
            <button type="button" class="kt-btn kt-btn-sm kt-btn-outline" (click)="openLinkModal()">
              Add linked records
            </button>
          </div>
        </div>

        <div class="kt-table-wrapper kt-scrollable">
          <table class="kt-table">
            <thead>
              <tr>
                <th scope="col">Concept</th>
                <th scope="col">Linked To</th>
                <th scope="col">Trade ID</th>
                <th scope="col">Deal ID</th>
                <th scope="col">Security ID</th>
                <th scope="col">Detail</th>
                <th scope="col" class="text-end">Amount</th>
                <th scope="col" class="w-24"></th>
              </tr>
            </thead>
            <tbody>
              @for (line of lineDrafts(); track line.key) {
                <tr>
                  <td>
                    <select
                      class="kt-select kt-select-sm"
                      [ngModel]="line.invConceptId"
                      (ngModelChange)="onLineFieldChange(line.key, 'invConceptId', $event)"
                    >
                      <option [ngValue]="null">Select a concept...</option>
                      @for (concept of availableInvoiceConceptOptions(); track concept.id) {
                        <option [ngValue]="concept.id">{{ concept.name }}</option>
                      }
                    </select>
                  </td>
                  <td>
                    @switch (line.linkTypeCode) {
                      @case ('DEAL') { Deal ID }
                      @case ('SECURITY') { Security ID }
                      @case ('BOND_DOCUMENTATION') { Program }
                      @default { Generic }
                    }
                  </td>
                  <td>{{ line.tradeId || '-' }}</td>
                  <td>{{ line.dealId || '-' }}</td>
                  <td>{{ line.securityId }}</td>
                  <td>
                    <input
                      type="text"
                      class="kt-input kt-input-sm"
                      placeholder="Optional detail"
                      [ngModel]="line.detail"
                      (ngModelChange)="onLineFieldChange(line.key, 'detail', $event)"
                    />
                  </td>
                  <td class="text-end">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      class="kt-input kt-input-sm text-end w-32"
                      placeholder="0.00"
                      [ngModel]="line.amountText"
                      (ngModelChange)="onLineFieldChange(line.key, 'amountText', $event)"
                    />
                  </td>
                  <td class="text-end">
                    <div class="flex items-center justify-end gap-1">
                      @if (line.linkTypeCode !== 'NONE') {
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-ghost"
                          title="Add another concept for this record"
                          (click)="duplicateLine(line.key)"
                        >+</button>
                      }
                      <button type="button" class="kt-btn kt-btn-sm kt-btn-ghost" (click)="removeLine(line.key)">Remove</button>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="8" class="text-center text-secondary-foreground py-6">
                    No invoice lines yet.
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="flex justify-end mt-3 text-sm text-secondary-foreground">
          Lines total: {{ formatAmount(form().currency, lineTotal()) }}
        </div>
      </div>

      <div class="invoice-lines">
        <div class="invoice-lines-header">
          <span>Invoice Document</span>
          <span class="invoice-required-chip">Required</span>
        </div>
        <div class="invoice-attachment-row">
          <input type="file" class="kt-input" [disabled]="isSubmitting()" (change)="onAttachmentSelected($any($event.target).files)" />
          @if (attachmentName()) {
            <div class="invoice-attachment-name">
              <span>{{ attachmentName() }}</span>
              @if (isSubmitting()) {
                <span class="text-xs text-secondary-foreground">Uploading...</span>
              } @else {
                <button type="button" class="kt-btn kt-btn-sm kt-btn-ghost" (click)="clearAttachment()">Remove</button>
              }
            </div>
          } @else {
            <div class="text-xs text-secondary-foreground">Attach the invoice file. Any document type is accepted.</div>
          }
        </div>
      </div>

      <div class="invoice-optional">
        <div class="invoice-optional-header">
          <div>
            <div class="invoice-optional-title">Additional Details</div>
            <div class="invoice-optional-sub">These fields are not required to save the invoice.</div>
          </div>
          <span class="invoice-optional-chip">Optional</span>
        </div>

        <div class="invoice-optional-grid">
          <label class="field-block">
            <span class="field-label">Service Date Start</span>
            <input
              type="date"
              class="kt-input"
              [ngModel]="form().serviceDateStart"
              (ngModelChange)="onFieldChange('serviceDateStart', $event)"
            />
          </label>

          <label class="field-block">
            <span class="field-label">Service Date End</span>
            <input
              type="date"
              class="kt-input"
              [ngModel]="form().serviceDateEnd"
              (ngModelChange)="onFieldChange('serviceDateEnd', $event)"
            />
          </label>

          <label class="field-block">
            <span class="field-label">Frequency</span>
            <select
              class="kt-select"
              [ngModel]="form().frequencyCode"
              (ngModelChange)="onFieldChange('frequencyCode', $event)"
            >
              <option value="">No frequency</option>
              @for (option of frequencyOptions(); track option) {
                <option [value]="option.code">{{ option.name }}</option>
              }
            </select>
          </label>

        </div>
      </div>

      <div class="invoice-optional">
        <div class="invoice-optional-header">
          <div>
            <div class="invoice-optional-title">Email Recipients</div>
            <div class="invoice-optional-sub">Add multiple email addresses separated by commas or semicolons.</div>
          </div>
          <span class="invoice-optional-chip">Optional</span>
        </div>
        <label class="field-block">
          <span class="field-label">Recipients</span>
          <input
            type="email"
            multiple
            class="kt-input"
            placeholder="name@example.com, another@example.com"
            [ngModel]="form().recipientEmail"
            (ngModelChange)="onFieldChange('recipientEmail', $event)"
          />
          <span class="text-xs text-secondary-foreground">Leave empty to use the configured default recipients.</span>
        </label>
      </div>

      <footer class="invoice-sheet-footer">
        <div class="invoice-total-box">
          <span class="invoice-total-label">Invoice Total</span>
          <span class="invoice-total-value">{{ formatAmount(form().currency, enteredInvoiceTotal()) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <a class="kt-btn kt-btn-outline" [class.pointer-events-none]="isSubmitting()" [routerLink]="['/invoices']">Cancel</a>
          <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSubmitting()" (click)="sendEmailOnly()">
            @if (submittingAction() === 'email') {
              <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
              Sending...
            } @else {
              Send Email
            }
          </button>
          <button type="button" class="kt-btn kt-btn-outline" [disabled]="isSubmitting()" (click)="submit()">
            @if (submittingAction() === 'save') {
              <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
              Saving...
            } @else {
              Save Invoice
            }
          </button>
          <button type="button" class="kt-btn kt-btn-primary" [disabled]="isSubmitting()" (click)="submitAndSendEmail()">
            @if (submittingAction() === 'save-send') {
              <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
              Saving & Sending...
            } @else {
              Save & Send
            }
          </button>
        </div>
      </footer>
    </section>
  </div>
</main>

@if (isLinkModalOpen()) {
  <app-related-records-selector
    [linkTypeCode]="selectedLinkTypeCode()"
    [bondDocumentationId]="form().linkedBondDocumentationId"
    [initialSelections]="modalInitialSelections()"
    (cancelled)="closeLinkModal()"
    (saved)="saveLinkModalSelection($event)"
  />
}
